// tv/primitives.jsx — small shared UI bits for TVShell.
//
// ChromeBtn       — 52px round button used along the top/bottom chrome
//                   rows of TVShell. Press state is driven by an outer
//                   `pressed` token so multiple buttons share one
//                   feedback channel.
// renderRocker    — volume rocker (−, slider, +) used for primary
//                   speaker volume and the pool-extension rocker.
//                   Renders nothing when `live` is null.
// AudioSliderRow  — single-slider audio control (centre / bass / treble
//                   / surround). Used by the audio panel disclosure.
// BrandBadge      — LG / Sony BRAVIA wordmark, rendered from imported
//                   SVGs in ./brands. Used in TVShell's now-playing
//                   strip header and the TV-menu disclosure corner.

import * as React from 'react';
import { I, Slider } from '../shared.jsx';
import { A_FONT, ALabel, BusyRing, fs } from '../direction-a.jsx';
import lgLogo from './brands/lg.svg';
import sonyBraviaLogo from './brands/sony-bravia.svg';

// Top/bottom chrome row button — a 52px quiet round key (cardSoft, no
// outline). Press feedback rules: pressed===k → a brief selected fill
// (joinery black in light, plaster in dark) with selectedInk glyph;
// `active` holds that same fill while the panel the button discloses
// (TV menu, keyboard bar, audio settings) is open.
//
// `busy` is for a button here that fires a multi-second chain rather
// than a single remote code. Power used to take it; since the 2026-10
// restyle Power renders the shared PowerButton (same busy colour +
// sweeping ring + halo as the room tile), and `busy` stays here so any
// other chain-firing chrome button gets the identical treatment.
// Everything else keeps the flash.
export function ChromeBtn({ T, k, pressed, onClick, onPress, disabled, busy = false, active = false, children, ariaLabel }) {
  const chosen = pressed === k || active;
  return (
    <button aria-label={ariaLabel} disabled={disabled}
      aria-busy={busy || undefined}
      onClick={() => { onPress?.(k); onClick?.(); }}
      style={{
        width: 52, height: 52, borderRadius: '50%', padding: 0, flexShrink: 0,
        border: 'none',
        background: busy ? T.busySoft : (chosen ? T.selected : T.cardSoft),
        color: busy ? T.busy : (chosen ? T.selectedInk : (disabled ? T.mute : T.ink)),
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background .12s, color .12s',
        // BusyRing overhangs by 3px on each side and is absolutely
        // positioned, so the host must be relative and unclipped.
        position: 'relative',
      }}>
      {busy && <BusyRing T={T} size={52}/>}
      {children}
    </button>
  );
}

// Skip key — 44px round quiet key for the playback row under the
// trackpad. Smaller than ChromeBtn's 52 because up to five controls
// share one row on a phone. `wide` is the long skip (six of the app's
// own steps) and gets a third chevron, which is the only honest way to
// draw it: the step belongs to the app, so no number of seconds can be
// printed on the button.
export const SKIP_GLYPH = {
  // One step, six, thirty: two, three and four triangles. Count alone
  // carries the size, so more always means further. An earlier draft
  // ended the largest on a bar, which is the universal "skip to the
  // end" mark and would have read as a different action entirely.
  // No seconds are printed on any of them — the step size belongs to
  // the app, not to the panel, so a number here would be invented.
  1:  'M4 5v14l8-7zM13 5v14l8-7z',
  6:  'M2 5v14l7-7zM10 5v14l7-7zM18 5v14l4-7z',
  30: 'M0 5v14l5-7zM6 5v14l5-7zM12 5v14l5-7zM18 5v14l5-7z',
};

export function SkipKey({ T, isOn, fwd = false, steps = 1, active = false, onClick, label }) {
  return (
    <button aria-label={label} disabled={!isOn} onClick={onClick}
      style={{
        width: 44, height: 44, borderRadius: '50%', padding: 0, flexShrink: 0, border: 'none',
        background: active ? T.selected : T.cardSoft,
        color: active ? T.selectedInk : (isOn ? T.ink : T.mute),
        cursor: isOn ? 'pointer' : 'not-allowed',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background .12s, color .12s',
        WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none',
      }}>
      <svg width={steps === 1 ? 20 : 23} height={steps === 1 ? 20 : 23} viewBox="0 0 24 24"
           fill="currentColor" style={{ transform: fwd ? 'none' : 'scaleX(-1)' }}>
        <path d={SKIP_GLYPH[steps] || SKIP_GLYPH[1]}/>
      </svg>
    </button>
  );
}

// Volume rocker — −/+ round keys step by 2, the shared 44px Slider in
// between, the level as a numeral on the right. Used for both primary
// and pool-extension rockers. The device name leads the row only when
// two rockers are showing.
export function renderRocker(T, live, entity, label, isOn, showLabel, sonosSpeakerCommand) {
  if (!live) return null;
  const step = (glyph, onClick) => (
    <button onClick={onClick} disabled={!isOn}
      style={{
        width: 48, height: 48, borderRadius: '50%', border: 'none', padding: 0,
        background: T.cardSoft, color: isOn ? T.ink : T.mute, flexShrink: 0,
        cursor: isOn ? 'pointer' : 'not-allowed',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{glyph}</button>
  );
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      opacity: isOn ? 1 : .5,
    }}>
      {showLabel && (
        <span style={{
          fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500, color: T.ink,
          minWidth: 64, flexShrink: 0, whiteSpace: 'nowrap',
        }}>
          {label}
        </span>
      )}
      {step(I.minus, () => sonosSpeakerCommand?.(entity, 'volume', Math.max(0, live.volume - 2)))}
      <div style={{ flex: 1, minWidth: 0, padding: '0 4px' }}>
        <Slider value={live.volume} relative
          onChange={(v) => sonosSpeakerCommand?.(entity, 'volume', v)}
          color={isOn ? T.ink : T.mute} track={T.cardSoft}/>
      </div>
      {step(I.plus, () => sonosSpeakerCommand?.(entity, 'volume', Math.min(100, live.volume + 2)))}
      <span style={{
        fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500, color: T.inkSoft,
        minWidth: 30, textAlign: 'right', flexShrink: 0,
        fontVariantNumeric: 'tabular-nums',
      }}>{live.volume}</span>
    </div>
  );
}

// Flat (0) draws in the tertiary tone so a boosted or cut control
// stands out from the rest at a glance.
export function AudioSliderRow({ T, c, onChange }) {
  return (
    <Slider value={c.value}
      min={c.min} max={c.max} step={c.step || 1}
      onChange={onChange}
      color={c.value === 0 ? T.mute : T.ink} track={T.cardSoft}/>
  );
}

// TV-brand wordmark — official LG + Sony BRAVIA logos rendered from
// imported SVGs. Falls back to capitalised text if the brand isn't
// mapped (defensive — should never hit in practice since tvBrandFor
// returns a known string). Sony BRAVIA logo is near-black (#231f20),
// which vanishes on the dark theme's card, so in dark the Sony variant
// alone is inverted to roughly the dark ink. LG's red-and-grey mark
// reads on both themes untouched.
export function BrandBadge({ T, brand, size = 'small' }) {
  const src = brand === 'LG' ? lgLogo
            : brand === 'BRAVIA' ? sonyBraviaLogo
            : null;
  if (!src) {
    return (
      <ALabel T={T}>{brand}</ALabel>
    );
  }
  // size='small' (22px tall) for inline header use; size='large' (32px)
  // for the bottom-right corner of the TV-menu card where it has room
  // to breathe and earns more presence.
  const h = size === 'large' ? 32 : 22;
  return (
    <img src={src} alt={brand}
      style={{
        height: h, width: 'auto', display: 'block',
        filter: T?.isDark && brand === 'BRAVIA' ? 'invert(.95)' : 'none',
      }}/>
  );
}
